
import React, { useMemo } from 'react';
import { isPhotoPlace, POINT_KINDS, pointKind } from '../services/placeModel';
import PlaceIcon from './PlaceIcon';
import { MapFeature, MapFeatureType, FarmTract, FarmField } from '../types';

export type FeatureCategory = 'Land' | 'Trail' | 'POI' | 'Structure';

interface MapFeatureEditorProps {
    feature: Partial<MapFeature>;
    onChange: (updates: Partial<MapFeature>) => void;
    tracts: FarmTract[];
    fields: FarmField[];
    farmId: string;
    showLabel?: boolean;
    variant?: 'default' | 'compact';
}

export const getCategoryFromFeature = (f: Partial<MapFeature>): FeatureCategory => {
    if (isPhotoPlace(f)) return f.feature_type === MapFeatureType.Structure ? 'Structure' : 'POI';
    if (f.feature_type === MapFeatureType.Trail) return 'Trail';
    if (f.feature_type === MapFeatureType.PointOfInterest) return 'POI';
    if (f.feature_type === MapFeatureType.Structure) return 'Structure';
    return 'Land'; // Default for Field, Tract, Farm
};

const MapFeatureEditor: React.FC<MapFeatureEditorProps> = ({ 
    feature, 
    onChange, 
    tracts, 
    fields, 
    farmId,
    showLabel = true,
    variant = 'default'
}) => {
    const category = useMemo(() => getCategoryFromFeature(feature), [feature.feature_type]);
    
    // Derived association string for the select input
    const associationValue = feature.entity_type && feature.entity_id 
        ? `${feature.entity_type}:${feature.entity_id}` 
        : (feature.entity_type === 'farm' ? `farm:${feature.entity_id}` : 'none:0');

    // -- Handlers --

    const handleCategoryChange = (newCat: FeatureCategory) => {
        const updates: Partial<MapFeature> = {};
        
        if (newCat === 'Land') {
            updates.fill_opacity = 0.2;
            updates.feature_type = MapFeatureType.Field; // Default
            updates.stroke_weight = 2; // Default thin border for land
            // Keep color
        } else {
            // Strict reset for non-land
            updates.fill_opacity = 0;
            updates.entity_type = undefined;
            updates.entity_id = undefined;
            
            if (newCat === 'Trail') {
                updates.feature_type = MapFeatureType.Trail;
                updates.stroke_weight = 3;
                if (!feature.color) updates.color = '#ff7800';
            } else if (newCat === 'POI') {
                updates.feature_type = MapFeatureType.PointOfInterest;
                updates.stroke_weight = 6;
                if (!feature.color) updates.color = '#9333ea';
            } else if (newCat === 'Structure') {
                updates.feature_type = MapFeatureType.Structure;
                updates.stroke_weight = 8;
                if (!feature.color) updates.color = '#6b7280';
            }
        }
        onChange(updates);
    };

    const handleAssociationChange = (val: string) => {
        const [type, idStr] = val.split(':');
        
        if (type === 'none') {
            onChange({ entity_type: undefined, entity_id: undefined });
        } else {
            const updates: Partial<MapFeature> = {
                entity_type: type as any,
                entity_id: idStr // string
            };
            
            // Auto-set feature type
            if (type === 'farm_field') updates.feature_type = MapFeatureType.Field;
            else if (type === 'farm_tract') updates.feature_type = MapFeatureType.Tract;
            else if (type === 'farm') updates.feature_type = MapFeatureType.Farm;

            // Auto-style for consistency
            if (type === 'farm_field') { updates.color = '#3388ff'; updates.fill_opacity = 0.2; }
            else if (type === 'farm_tract') { updates.color = '#fbbf24'; updates.fill_opacity = 0; updates.stroke_weight = 2; }
            
            onChange(updates);
        }
    };

    const isCompact = variant === 'compact';

    return (
        <div className={`space-y-4 ${isCompact ? 'text-sm' : ''}`}>
            
            {showLabel && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Label</label>
                    <input 
                        type="text" aria-label="Place name"
                        value={feature.label || ''} 
                        onChange={(e) => onChange({ label: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 text-sm p-1.5 border"
                    />
                </div>
            )}

            {isPhotoPlace(feature) && <div><label className="block text-xs font-medium text-gray-500 mb-2">Point type</label>
              <div className="grid grid-cols-3 gap-2">{POINT_KINDS.map(kind => <button key={kind.value} type="button" aria-pressed={pointKind(feature) === kind.value}
                onClick={() => onChange({ point_kind: kind.value, feature_type: ['barn','outbuilding','cabin'].includes(kind.value) ? MapFeatureType.Structure : MapFeatureType.PointOfInterest, is_inactive:feature.is_inactive||false })}
                className={'flex flex-col items-center gap-1 rounded-lg border p-2 text-xs '+(pointKind(feature) === kind.value ? 'border-usda-600 bg-usda-50 ring-1 ring-usda-600' : 'border-gray-200 hover:bg-gray-50')}>
                <PlaceIcon place={{point_kind:kind.value}} />{kind.label}</button>)}</div></div>}
            {isPhotoPlace(feature)&&<label className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 p-3 text-sm"><span><span className="font-medium block">Inactive point</span><span className="text-xs text-gray-500">Keep this location and its photos, shown in gray.</span></span><input aria-label="Inactive point" type="checkbox" checked={!!feature.is_inactive} onChange={e=>onChange({is_inactive:e.target.checked})}/></label>}
            {/* Category Selector */}
            <div hidden={isPhotoPlace(feature)}>
                <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                <div className="grid grid-cols-4 gap-2">
                    {(isPhotoPlace(feature) ? [] : ['Land', 'Trail', 'POI', 'Structure'] as FeatureCategory[]).map((cat) => (
                        <button
                            key={cat}
                            type="button"
                            onClick={() => handleCategoryChange(cat)}
                            className={`py-1.5 text-xs font-medium rounded border transition-colors ${
                                category === cat 
                                ? 'bg-usda-600 text-white border-usda-600' 
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Conditional Controls */}
            {category === 'Land' && (
                <div className={`rounded-md border border-blue-100 bg-blue-50 ${isCompact ? 'p-2' : 'p-3'}`}>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Linked Record</label>
                    <select 
                        value={associationValue} 
                        onChange={(e) => handleAssociationChange(e.target.value)}
                        className="block w-full rounded border-blue-200 text-xs p-1.5 focus:border-blue-500 focus:ring-blue-500 mb-2"
                    >
                        <option value="none:0">-- Generic Land (No Link) --</option>
                        <option value={`farm:${farmId}`}>Farm Boundary</option>
                        <optgroup label="Tracts">
                            {tracts.map(t => <option key={t.tract_id} value={`farm_tract:${t.tract_id}`}>Tract {t.usda_tract_number}</option>)}
                        </optgroup>
                        <optgroup label="Fields">
                            {fields.map(fl => <option key={fl.field_id} value={`farm_field:${fl.field_id}`}>Field {fl.usda_field_number}</option>)}
                        </optgroup>
                    </select>
                    
                    <div className="flex items-center space-x-2">
                        <label className="text-[10px] uppercase font-bold text-blue-800 w-12">Opacity</label>
                        <input 
                            type="range" min="0" max="1" step="0.1" 
                            value={feature.fill_opacity !== undefined ? feature.fill_opacity : 0.2} 
                            onChange={(e) => onChange({ fill_opacity: parseFloat(e.target.value) })}
                            className="flex-1 h-1.5 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-xs text-blue-800 w-8 text-right">
                            {Math.round((feature.fill_opacity !== undefined ? feature.fill_opacity : 0.2) * 100)}%
                        </span>
                    </div>
                </div>
            )}

            {/* Appearance Row */}
            <div style={{display:isPhotoPlace(feature)?'none':undefined}} className={`grid grid-cols-2 gap-4 ${isCompact ? 'pt-1' : ''}`}>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Color</label>
                    <div className="flex items-center space-x-2">
                        <input 
                            type="color" 
                            value={feature.color || '#3388ff'} 
                            onChange={(e) => onChange({ color: e.target.value, fill_color: e.target.value })}
                            className="h-8 w-12 p-0 border border-gray-300 rounded cursor-pointer"
                        />
                    </div>
                </div>
                
                <div hidden={isPhotoPlace(feature)}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                        {category === 'POI' || category === 'Structure' ? 'Size' : 'Width'} ({feature.stroke_weight || 2}px)
                    </label>
                    <input 
                        type="range" min="1" max="20" 
                        value={feature.stroke_weight || 2}
                        onChange={(e) => onChange({ stroke_weight: parseInt(e.target.value) })}
                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
            </div>
        </div>
    );
};

export default MapFeatureEditor;
